import { Menu as BaseMenu } from "@base-ui/react/menu";
import clsx from "clsx";
import type React from "react";
import { forwardRef, type ReactNode, useRef } from "react";
import { MenuModal } from "./MenuModal";
import { MenuLinkProvider } from "./menuLinkContext";
import {
  fillerClass,
  itemContainerClass,
  menuContainerClass,
} from "./styles.css";

export interface MenuItemProps {
  id: string;
  text: string;
  path?: string;
  icon?: ReactNode;
  subitems?: MenuItemProps[];
  disabled?: boolean;
  condition?: boolean;
  external?: string;
  overridePath?: string;
  filler?: boolean;
  height?: number;
  searchText?: string;
  isSubsubitem?: boolean;
  isLastSubsubitem?: boolean;
}

interface MenuProps extends React.HTMLAttributes<HTMLDivElement> {
  menuItems: MenuItemProps[];
  linkComponent?: React.ElementType;
}

interface MenuItemWithModalProps {
  item: MenuItemProps;
  menuItems: MenuItemProps[];
  location: string;
}

const MenuItemWithModal = ({
  item,
  menuItems,
  location,
}: MenuItemWithModalProps) => {
  const actionsRef = useRef<BaseMenu.Root.Actions>(null);

  return (
    <BaseMenu.Root actionsRef={actionsRef} modal={false}>
      <BaseMenu.Trigger
        render={
          <a
            id={`menu-item-${item.id}`}
            href="#"
            className={itemContainerClass}
            aria-selected={location === item.path?.split("/")[1]}
          />
        }
        nativeButton={false}
      >
        {item.icon}
        <span>{item.text}</span>
      </BaseMenu.Trigger>
      <MenuModal
        item={item}
        menuItems={menuItems}
        onClose={() => actionsRef.current?.close()}
      />
    </BaseMenu.Root>
  );
};

export const Menu = forwardRef(function Menu(
  { menuItems = [], linkComponent = "a", className, ...props }: MenuProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const location = window.location.pathname.split("/")[1];

  return (
    <MenuLinkProvider component={linkComponent}>
      <div ref={ref} {...props} className={clsx(menuContainerClass, className)}>
        {menuItems.map((item) => {
          if (item.condition === false) return;

          if (item.filler) {
            return (
              <div
                key={item.id}
                className={fillerClass}
                style={{ height: `${item.height || 20}px` }}
              />
            );
          }

          const hasSubitems = item.subitems && item.subitems.length > 0;
          const isSearchItem = item.id.includes("search");

          if (hasSubitems || isSearchItem) {
            return (
              <MenuItemWithModal
                key={item.id}
                item={item}
                menuItems={menuItems}
                location={location}
              />
            );
          }

          const pathToGo = item.external ? "#" : item.path || "#";

          return (
            <a
              key={item.id}
              id={`menu-item-${item.id}`}
              href={pathToGo}
              className={itemContainerClass}
              aria-selected={location === item.path?.split("/")[1]}
              onClick={(e) => {
                e.stopPropagation();
                if (item.external) window.open(item.external, "_blank");
              }}
            >
              {item.icon}
              <span>{item.text}</span>
            </a>
          );
        })}
      </div>
    </MenuLinkProvider>
  );
});

Menu.displayName = "Menu";
